/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiGet, TENANT_TOKEN_KEY } from '../api/client';
import { hasPermission, type SessionMe } from './permissions';

type Ctx = {
  loading: boolean;
  permissions: Set<string>;
  session: SessionMe | null;
  can: (required: string | string[]) => boolean;
  reload: () => Promise<void>;
};

const TenantPermissionsContext = createContext<Ctx | null>(null);

export function TenantPermissionsProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<SessionMe | null>(null);

  const reload = useCallback(async () => {
    const token = localStorage.getItem(TENANT_TOKEN_KEY);
    if (!token) {
      setSession(null);
      setLoading(false);
      return;
    }
    try {
      const data = await apiGet<SessionMe>('/api/auth/me', token, { showErrorGrowl: false });
      setSession(data);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const permissions = useMemo(
    () => new Set(session?.permissions ?? []),
    [session?.permissions]
  );

  const can = useCallback(
    (required: string | string[]) => hasPermission(permissions, required),
    [permissions]
  );

  const value = useMemo(
    () => ({ loading, permissions, session, can, reload }),
    [loading, permissions, session, can, reload]
  );

  return (
    <TenantPermissionsContext.Provider value={value}>{children}</TenantPermissionsContext.Provider>
  );
}

export function useTenantPermissions(): Ctx {
  const c = useContext(TenantPermissionsContext);
  if (!c) throw new Error('useTenantPermissions fuera de TenantPermissionsProvider');
  return c;
}
